import { useState, useEffect, useMemo, useRef } from "react";
import { ValidateInvoiceUseCase } from "../../domain/use-cases";
import { ValidationRepositoryImpl } from "../../data/repositories";
import {
  ValidationResponse,
  ValidationData,
} from "../../domain/entities/ValidationEntity";
import { InvoiceEntity } from "../../domain/entities/InvoiceEntity";
import {
  AI_DETECTION_MAX_RETRIES,
  AI_DETECTION_RETRY_BASE_DELAY_MS,
  sleep,
} from "../../../../shared/constants/aiDetectionMessages";

// Initialize the use case with repository
const validationRepository = new ValidationRepositoryImpl();
const validateInvoiceUseCase = new ValidateInvoiceUseCase(validationRepository);

interface UseInvoiceValidationProps {
  invoiceData: any;
  invoiceDocument: InvoiceEntity;
  onValidationComplete?: (result: ValidationResponse) => void;
}

export const useInvoiceValidation = ({
  invoiceData,
  invoiceDocument,
  onValidationComplete,
}: UseInvoiceValidationProps) => {
  const [validationData, setValidationData] = useState<ValidationData | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastValidatedDataRef = useRef<string | null>(null);
  const [isFirstValidation, setIsFirstValidation] = useState(true);

  // Cooldown: skip re-validation when invoiceData changed only because our callback
  // updated the parent (tax_ids, etc.). Prevents double API call.
  const lastValidationCompletedAtRef = useRef<number>(0);
  const VALIDATION_COOLDOWN_MS = 1500;

  // Store the latest callback in a ref to avoid re-running effect when callback changes
  const onValidationCompleteRef = useRef(onValidationComplete);

  // Update the ref whenever the callback changes (but don't trigger effect re-run)
  useEffect(() => {
    onValidationCompleteRef.current = onValidationComplete;
  }, [onValidationComplete]);

  // Memoize the invoiceData to prevent unnecessary re-renders
  const memoizedInvoiceData = useMemo(() => {
    return invoiceData ? JSON.stringify(invoiceData) : null;
  }, [invoiceData]);

  // Run validation when component mounts or invoiceData actually changes
  useEffect(() => {
    let cancelled = false;

    const now = Date.now();
    const isInCooldown =
      now - lastValidationCompletedAtRef.current < VALIDATION_COOLDOWN_MS;

    // Only show loading if we have new data to validate AND we're not in cooldown.
    // In cooldown, invoiceData changed because our callback updated the parent (tax);
    // don't flash "Running AI Detection..." — keep showing the existing results.
    if (
      memoizedInvoiceData &&
      memoizedInvoiceData !== lastValidatedDataRef.current &&
      !isInCooldown
    ) {
      setLoading(true);
      setError(null);
    }
    if (
      memoizedInvoiceData &&
      memoizedInvoiceData !== lastValidatedDataRef.current &&
      isInCooldown
    ) {
      lastValidatedDataRef.current = memoizedInvoiceData;
    }

    const runValidation = async () => {
      if (
        !memoizedInvoiceData ||
        memoizedInvoiceData === lastValidatedDataRef.current
      ) {
        setLoading(false);
        return;
      }

      // If we just completed a validation, the parent likely updated invoiceJson
      // from our callback (tax_ids). Don't run the API again.
      const nowInRun = Date.now();
      if (
        nowInRun - lastValidationCompletedAtRef.current <
        VALIDATION_COOLDOWN_MS
      ) {
        lastValidatedDataRef.current = memoizedInvoiceData;
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      const validationType = isFirstValidation
        ? "full_validation"
        : "normal_validation";
      let attempt = 0;
      let lastError: string | null = null;

      while (attempt <= AI_DETECTION_MAX_RETRIES) {
        if (cancelled) return;

        attempt++;
        try {
          const response = await validateInvoiceUseCase.execute(
            JSON.parse(memoizedInvoiceData),
            invoiceDocument,
            validationType,
          );

          if (response.success) {
            setValidationData(response.data);
            lastValidatedDataRef.current = memoizedInvoiceData;
            lastValidationCompletedAtRef.current = Date.now();
            onValidationCompleteRef.current?.(response);
            if (isFirstValidation) {
              setIsFirstValidation(false);
            }
            setLoading(false);
            return;
          }

          lastError = "Validation failed";
        } catch (err) {
          lastError = "Error running validation";
          console.error(
            `Validation error (attempt ${attempt}/${AI_DETECTION_MAX_RETRIES + 1}):`,
            err,
          );
        }

        if (attempt <= AI_DETECTION_MAX_RETRIES) {
          await sleep(AI_DETECTION_RETRY_BASE_DELAY_MS * attempt);
        }
      }

      if (!cancelled) {
        setError(lastError);
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(runValidation, 500);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [memoizedInvoiceData, invoiceDocument, isFirstValidation]);

  // Reset when invoiceDocument changes (user navigates to different document)
  useEffect(() => {
    setIsFirstValidation(true);
    lastValidationCompletedAtRef.current = 0;
    lastValidatedDataRef.current = null;
    setError(null);
  }, [invoiceDocument?.jobid]);

  return {
    validationData,
    loading,
    error,
  };
};
